import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import type { AxiosError } from 'axios';

export const HTTP_STATUS = { NOT_FOUND: 404 } as const;
export type HttpStatus = (typeof HTTP_STATUS)[keyof typeof HTTP_STATUS];

/**
 * Custom error class for API errors with structured detail.
 */
export class ApiError extends Error {
  readonly detail: ApiErrorDetail;

  constructor(detail: ApiErrorDetail) {
    super(detail.message);
    this.name = 'ApiError';
    this.detail = detail;
  }
}

/**
 * Extract structured error detail from an unknown error.
 * Works with AxiosError, ApiError, plain Error, and unknown shapes.
 */
export function getApiErrorDetail(error: unknown): ApiErrorDetail {
  if (error instanceof ApiError) {
    return error.detail;
  }

  if (isAxiosError(error)) {
    return extractFromAxiosError(error);
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: 'An unknown error occurred' };
}

/**
 * Returns true when `error` is an Axios-shaped error carrying a specific HTTP status.
 * Safe to call with any unknown value — never throws.
 */
export function isHttpStatus(error: unknown, status: number): boolean {
  return isAxiosError(error) && error.response?.status === status;
}

/** Runs `fn`, turning a 404 into `null` — the shared "resource not created yet" read pattern. */
export async function nullOnNotFound<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    if (isHttpStatus(error, HTTP_STATUS.NOT_FOUND)) {
      return null;
    }
    throw error;
  }
}

/**
 * Returns true when `error` is an Axios-shaped error. Safe to call with any unknown
 * value — never throws. Shared by callers that need to narrow before reading
 * Axios-specific fields (e.g. `config`, `response`) instead of casting.
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return error !== null && typeof error === 'object' && (error as AxiosError).isAxiosError === true;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractFromAxiosError(error: AxiosError): ApiErrorDetail {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const status = error.response?.status;

  if (!data || typeof data !== 'object') {
    return {
      message: error.message ?? 'Request failed',
      statusCode: status,
    };
  }

  return buildDetailFromResponseData(data, error.message, status);
}

function buildDetailFromResponseData(
  data: Record<string, unknown>,
  fallbackMessage: string,
  status: number | undefined,
): ApiErrorDetail {
  const message = typeof data['message'] === 'string' ? data['message'] : fallbackMessage;
  const statusCode = typeof data['statusCode'] === 'number' ? data['statusCode'] : status;
  const correlationId = typeof data['correlationId'] === 'string' ? data['correlationId'] : undefined;
  const errorCode = typeof data['errorCode'] === 'string' ? data['errorCode'] : undefined;
  const details =
    typeof data['details'] === 'object' && data['details'] !== null
      ? (data['details'] as Record<string, unknown>)
      : undefined;

  return { correlationId, details, errorCode, message, statusCode };
}
